import { useParams, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { VERIFY_SESSION } from "@auth/graphql/mutations/verifySession";
import { FETCH_USER_USAGE } from "@company/graphql/queries/fetchUserUsage";
import { Box, Typography, Button, Card, CardContent, CircularProgress, useTheme } from "@mui/material";
import { icon } from "@/app/style/icons";
import SeatSelectionDialog from "../components/SeatSelectionDialog";
import { useCheckoutPersistence } from "../hooks/useCheckoutPersistence";

const CheckoutStripeReturnPage = () => {
    const theme = useTheme();
    const { sessionId } = useParams<{ sessionId: string }>();
    const [verifySessionMutation, { loading, data, error }] = useMutation(VERIFY_SESSION);
    const [status, setStatus] = useState<"loading" | "success" | "error" | "canceled">("loading");
    const [sessionDetails, setSessionDetails] = useState<any>(null);
    const [showSeatSelection, setShowSeatSelection] = useState(false);
    const [selectedSeat, setSelectedSeat] = useState<string>("");
    const { data: userUsage } = useQuery(FETCH_USER_USAGE, {
        skip: status !== "success" || !sessionDetails?.seatSelection?.showSelection,
    });
    const location = useLocation();
    const isCancel = location.pathname.includes("/payment/cancel/");
    const { clearCheckoutState } = useCheckoutPersistence();

    useEffect(() => {
        if (isCancel) {
            // Handle canceled payment - no need to verify with backend
            setStatus("canceled");
            return;
        }

        if (sessionId) {
            verifySessionMutation({
                variables: {
                    sessionId: sessionId,
                },
            })
                .then(({ data }) => {
                    console.log("Verify session response:", data);
                    setSessionDetails(data.verifySession);
                    setStatus("success");

                    // Clear any persisted checkout state now that payment succeeded
                    clearCheckoutState();

                    // Check if seat selection should be shown (with delay to let user see success message)
                    if (data.verifySession.seatSelection?.showSelection) {
                        setTimeout(() => {
                            setShowSeatSelection(true);
                        }, 2000); // Show dialog after 2 seconds
                    }
                })
                .catch((err) => {
                    console.error("Error verifying session:", err);
                    setStatus("error");
                });
        }
    }, [sessionId, verifySessionMutation, isCancel, clearCheckoutState]);

    if (loading || status === "loading") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "70vh",
                    p: 3,
                }}
            >
                <Card sx={{ maxWidth: 480, width: "100%", boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                            <CircularProgress size={32} color="primary" />
                        </Box>
                        <Typography variant="h5" align="center" sx={{ fontWeight: 500, mb: 2 }}>
                            Zahlungsinformationen werden verarbeitet
                        </Typography>
                        <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 2 }}>
                            Bitte warten Sie, während wir Ihre Zahlung bestätigen.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (status === "canceled") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "70vh",
                    p: 3,
                }}
            >
                <Card sx={{ maxWidth: 480, width: "100%", boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                            <icon.block sx={{ fontSize: 32, color: theme.palette.semantic.button.primary }} />
                        </Box>
                        <Typography variant="h5" align="center" sx={{ fontWeight: 500, mb: 2, color: theme.palette.semantic.button.primary }}>
                            Zahlung abgebrochen
                        </Typography>
                        <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
                            Sie haben den Zahlungsvorgang abgebrochen. Keine Belastung wurde durchgeführt.
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button component={Link} to="/dashboard" variant="contained" color="primary" sx={{ px: 3, py: 1 }}>
                                Zurück zum Dashboard
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (error || status === "error") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "70vh",
                    p: 3,
                }}
            >
                <Card sx={{ maxWidth: 480, width: "100%", boxShadow: 2, borderRadius: 2 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                            <icon.error sx={{ fontSize: 32, color: "semantic.notification.error" }} />
                        </Box>
                        <Typography variant="h5" align="center" sx={{ fontWeight: 500, mb: 2, color: "semantic.notification.error" }}>
                            Fehler bei der Verarbeitung
                        </Typography>
                        <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
                            Leider ist ein Fehler aufgetreten. Bitte kontaktieren Sie unseren Support.
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button component={Link} to="/dashboard" variant="contained" color="primary" sx={{ px: 3, py: 1 }}>
                                Zurück zum Dashboard
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Erfolgsfall
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "70vh",
                p: 3,
            }}
        >
            <Card sx={{ maxWidth: 480, width: "100%", boxShadow: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                        <icon.success sx={{ fontSize: 32, color: theme.palette.semantic.button.primary }} />
                    </Box>
                    <Typography variant="h5" align="center" sx={{ fontWeight: 500, mb: 2, color: theme.palette.semantic.button.primary }}>
                        Zahlung erfolgreich!
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                        Vielen Dank für Ihren Einkauf. Ihre Zahlung wurde erfolgreich verarbeitet.
                    </Typography>

                    {sessionDetails && sessionDetails.mode === "subscription" && (
                        <Box sx={{ bgcolor: theme.palette.semantic.button.primary, borderRadius: 1, p: 2, mb: 3, border: `1px solid ${theme.palette.semantic.button.primary}20` }}>
                            <Typography variant="body2" align="center" color={"white"}>
                                Ihr Abonnement wurde aktiviert. Sie haben jetzt Zugriff auf alle Funktionen.
                            </Typography>
                        </Box>
                    )}

                    {sessionDetails?.seatSelection?.showSelection && (
                        <Box sx={{ bgcolor: theme.palette.info.light, borderRadius: 1, p: 2, mb: 3, border: `1px solid ${theme.palette.info.main}20` }}>
                            <Typography variant="body2" align="center" sx={{ fontWeight: 500, mb: 1 }}>
                                🎯 Mehrere Sitzplatztypen verfügbar
                            </Typography>
                            <Typography variant="body2" align="center" color="white">
                                Sie haben verschiedene Sitzplatztypen erworben. Wählen Sie den gewünschten Typ für Ihr Konto aus.
                            </Typography>
                        </Box>
                    )}

                    {sessionDetails && sessionDetails.mode === "payment" && (
                        <Box sx={{ bgcolor: theme.palette.semantic.button.primary, borderRadius: 1, p: 2, mb: 3, border: `1px solid ${theme.palette.semantic.button.primary}20` }}>
                            <Typography variant="body2" align="center" color={"white"}>
                                Ihre Service-Credits wurden Ihrem Konto gutgeschrieben und können sofort verwendet werden.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        {sessionDetails?.seatSelection?.showSelection && (
                            <Button onClick={() => setShowSeatSelection(true)} variant="outlined" color="primary" sx={{ px: 3, py: 1 }}>
                                Sitzplatz wählen
                            </Button>
                        )}
                        <Button component={Link} to="/dashboard" variant="contained" color="primary" sx={{ px: 3, py: 1 }}>
                            Zurück zum Dashboard
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Seat Selection Dialog */}
            {sessionDetails?.seatSelection && (
                <SeatSelectionDialog
                    open={showSeatSelection}
                    onClose={() => setShowSeatSelection(false)}
                    currentSeat={sessionDetails.seatSelection.currentSeat}
                    availableSeats={sessionDetails.seatSelection.availableSeats}
                    currentUsedPages={userUsage?.userProfile?.usedPages || 0}
                    currentQuota={userUsage?.userProfile?.pageQuota || 0}
                    onSuccess={(newSeatType) => {
                        setSelectedSeat(newSeatType);
                        console.log(`Seat type changed to: ${newSeatType}`);
                        // Optionally show a success message or update the UI
                    }}
                />
            )}
        </Box>
    );
};

export default CheckoutStripeReturnPage;
